import React from "react";

/**
 * CardWatch Logo — 3D-style minimalist SVG.
 * Combines a credit card (rounded rectangle with chip + magnetic stripe)
 * with a watch motif (circular dial + tick marks + hands) sitting on the card.
 * Uses linearGradients + radialGradient + feDropShadow for depth (not flat).
 */
export const CardWatchMark = ({ size = 44, className = "" }) => {
  const uid = React.useId().replace(/:/g, "");
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`card-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1E3A8A" />
          <stop offset="55%" stopColor="#0F1E4A" />
          <stop offset="100%" stopColor="#060B19" />
        </linearGradient>
        <linearGradient id={`edge-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3B5BDB" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#00E676" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`chip-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00E676" />
          <stop offset="100%" stopColor="#009B4D" />
        </linearGradient>
        <radialGradient id={`dial-${uid}`} cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#0E172C" />
          <stop offset="70%" stopColor="#060B19" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>
        <linearGradient id={`bezel-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00E676" />
          <stop offset="100%" stopColor="#008C46" />
        </linearGradient>
        <filter id={`shadow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#000000" floodOpacity="0.55" />
        </filter>
        <filter id={`glow-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="0" stdDeviation="1.6" floodColor="#00E676" floodOpacity="0.7" />
        </filter>
      </defs>

      {/* Credit card body with 3D depth */}
      <g filter={`url(#shadow-${uid})`}>
        <rect x="6" y="14" width="52" height="36" rx="6" ry="6" fill={`url(#card-${uid})`} />
        {/* Top light streak for gloss */}
        <rect x="6" y="14" width="52" height="9" rx="6" ry="6" fill={`url(#edge-${uid})`} opacity="0.6" />
        {/* Magnetic stripe */}
        <rect x="6" y="24" width="52" height="4" fill="#000" opacity="0.65" />
        {/* Chip */}
        <rect x="12" y="33" width="9" height="7" rx="1.4" fill={`url(#chip-${uid})`} />
        <path d="M12 36.5 h9 M16.5 33 v7" stroke="#0E172C" strokeWidth="0.6" opacity="0.7" />
      </g>

      {/* Watch dial on the right side of card (raised) */}
      <g filter={`url(#shadow-${uid})`}>
        <circle cx="44" cy="36" r="11" fill={`url(#bezel-${uid})`} />
        <circle cx="44" cy="36" r="9" fill={`url(#dial-${uid})`} />
        {/* Tick marks */}
        <g stroke="#00E676" strokeWidth="1.1" strokeLinecap="round">
          <line x1="44" y1="28.5" x2="44" y2="30" />
          <line x1="44" y1="42" x2="44" y2="43.5" />
          <line x1="36.5" y1="36" x2="38" y2="36" />
          <line x1="50" y1="36" x2="51.5" y2="36" />
        </g>
        {/* Watch hands */}
        <g filter={`url(#glow-${uid})`}>
          <line x1="44" y1="36" x2="44" y2="30.5" stroke="#FFFFFF" strokeWidth="1.4" strokeLinecap="round" />
          <line x1="44" y1="36" x2="48.5" y2="36" stroke="#00E676" strokeWidth="1.4" strokeLinecap="round" />
        </g>
        <circle cx="44" cy="36" r="1.2" fill="#00E676" />
      </g>
    </svg>
  );
};

export const CardWatchLogo = ({ size = 40, className = "", showText = true }) => {
  return (
    <div className={`flex items-center gap-3 ${className}`} data-testid="cardwatch-logo">
      <CardWatchMark size={size} />
      {showText && (
        <span
          className="text-white font-semibold tracking-tight"
          style={{ fontFamily: "'Outfit', sans-serif", fontSize: size * 0.55, lineHeight: 1 }}
        >
          Card<span style={{ color: "#00E676" }}>Watch</span>
        </span>
      )}
    </div>
  );
};

export default CardWatchLogo;
